/**
 * Read + mutate the user's learned insights (Role / Focus / Work patterns).
 *
 * Wraps the shared backend endpoint
 * ``/api/orgs/<orgId>/learning/users/<userId>/insights/``:
 *
 *   - **GET** returns ``{items: InsightItem[]}`` in canonical INSIGHT_FIELDS
 *     order. Same payload the welcome onboarding proposal embeds.
 *   - **PATCH** updates a single field by ``{term, value}``.
 *
 * Used by the agent-guidance settings section so the user can keep editing
 * their learned context after dismissing the welcome onboarding flow.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiGet, apiPatch } from '@/lib/http/client';

export interface InsightItem {
  term: string;
  definition: string;
  editable: boolean;
  multiline: boolean;
}

interface InsightsResponse {
  items: InsightItem[];
}

interface UpdateInsightVariables {
  term: string;
  value: string;
}

/**
 * Stable key factory keeps mutation invalidation paired with the query.
 * Exported so consumers (e.g. tests) can target the cache directly.
 */
export const learnedInsightsQueryKey = (
  orgId: number | undefined,
  userId: number | undefined
) => ['learnedInsights', orgId, userId] as const;

export function useLearnedInsights(
  orgId: number | undefined,
  userId: number | undefined
) {
  return useQuery({
    queryKey: learnedInsightsQueryKey(orgId, userId),
    queryFn: () =>
      apiGet<InsightsResponse>(
        `/api/orgs/${orgId}/learning/users/${userId}/insights/`
      ),
    enabled: !!orgId && !!userId,
    // Insights only change on a user edit (which invalidates the cache via
    // the mutation below) or after a background learning agent finishes. The
    // welcome-proposal flow has its own polling for the latter; the settings
    // page is a viewing-after-the-fact surface so a stable stale time is
    // fine here.
    staleTime: 5 * 60_000,
  });
}

export function useUpdateLearnedInsight(
  orgId: number | undefined,
  userId: number | undefined
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ term, value }: UpdateInsightVariables) =>
      apiPatch<{
        term: string;
        value: string;
        profile_type: string;
        key: string;
      }>(`/api/orgs/${orgId}/learning/users/${userId}/insights/`, {
        term,
        value,
      }),
    onSuccess: () => {
      // Re-fetch so the rendered values reflect server truth. Also keeps
      // the welcome proposal in sync if it happens to still be visible.
      queryClient.invalidateQueries({
        queryKey: learnedInsightsQueryKey(orgId, userId),
      });
    },
  });
}
